import { useEffect, useMemo, useState } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { where } from 'firebase/firestore'
import { app } from '../lib/firebase'
import { useAuth } from '../lib/auth'
import { useCollection } from '../lib/firestore'
import { createItineraryItem } from '../lib/mutations'
import { geocodeVenue } from '../lib/geocode'
import { formatDayLong } from '../lib/format'
import type { Show, Tour, WithId } from '../types/models'
import Modal from './Modal'
import Icon from './Icon'

/* ============================================================================
   Route builder.

   Build a journey as a list of stops. Between every pair of stops sits a leg,
   and a leg is either driven or flown.

   Two decisions shape the whole thing:

   ONE SCREEN. Not a wizard. Touring plans change at four in the afternoon on
   the day, and a five-step flow you have to complete is a flow nobody opens
   the second time. Add a stop, change a mode, set a time, save — in any order,
   and half-finished is a legitimate place to stop.

   STOPS ARE PLACES, NOT SHOWS. A show is the commonest stop, so it is one tap
   from the picker, but the airport you connect through and the hotel you break
   the drive at are stops too. Restricting the builder to shows would mean it
   couldn't describe a real journey.

   What it writes is ordinary itinerary items — the same records the app
   already reads everywhere. There is no route document to fall out of sync,
   which is the same reason the planner reads rather than stores.
   ============================================================================ */

const routeBetween = httpsCallable<
  { from: { lat: number; lon: number }; to: { lat: number; lon: number } },
  { found: boolean; km: number | null; hours: number | null; totalHours?: number; breaks?: number }
>(getFunctions(app), 'routeBetween')

type Mode = 'drive' | 'fly'
type Anchor = 'leave' | 'arrive'

interface Stop {
  /** Stable across reorders, so React and the leg cache stay aligned. */
  key: string
  label: string
  detail: string
  lat: number | null
  lon: number | null
  showId?: string
  /** yyyy-mm-dd. Seeded from a show, editable for a typed stop. */
  date: string
}

interface LegState {
  mode: Mode
  anchor: Anchor
  /** "09:00" */
  time: string
  km: number | null
  hours: number | null
  loading: boolean
}

export default function RouteBuilder({
  tour,
  onClose,
}: {
  tour: WithId<Tour>
  onClose: () => void
}) {
  const { uid } = useAuth()
  const shows = useCollection<Show>('shows', [where('tourId', '==', tour.id)])

  const [stops, setStops] = useState<Stop[]>([])
  const [legs, setLegs] = useState<Record<string, LegState>>({})
  const [picking, setPicking] = useState(false)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sortedShows = useMemo(
    () => [...shows.data].sort((a, b) => a.date.toMillis() - b.date.toMillis()),
    [shows.data],
  )

  const legKey = (a: Stop, b: Stop) => `${a.key}>${b.key}`

  // Drive times are fetched per leg as the chain changes. Only legs that are
  // driven and have both ends located get costed.
  useEffect(() => {
    stops.slice(0, -1).forEach((from, index) => {
      const to = stops[index + 1]
      const key = legKey(from, to)
      const state = legs[key]
      if (!state || state.mode !== 'drive' || state.km !== null || state.loading) return
      if (from.lat === null || to.lat === null) return

      setLegs((current) => ({ ...current, [key]: { ...current[key], loading: true } }))
      void routeBetween({
        from: { lat: from.lat, lon: from.lon! },
        to: { lat: to.lat, lon: to.lon! },
      })
        .then((response) => {
          const { found, km, totalHours } = response.data
          setLegs((current) => ({
            ...current,
            [key]: {
              ...current[key],
              loading: false,
              km: found ? km : null,
              hours: found ? (totalHours ?? null) : null,
            },
          }))
        })
        .catch(() => {
          setLegs((current) => ({ ...current, [key]: { ...current[key], loading: false } }))
        })
    })
  }, [stops, legs])

  function addStop(stop: Stop) {
    setStops((current) => {
      const next = [...current, stop]
      const previous = current[current.length - 1]
      if (previous) {
        const key = `${previous.key}>${stop.key}`
        setLegs((state) => ({
          ...state,
          [key]: { mode: 'drive', anchor: 'arrive', time: '', km: null, hours: null, loading: false },
        }))
      }
      return next
    })
    setPicking(false)
    setTyped('')
  }

  function addShow(show: WithId<Show>) {
    addStop({
      key: `show-${show.id}`,
      label: show.city,
      detail: show.venueName,
      lat: show.venueLat ?? null,
      lon: show.venueLon ?? null,
      showId: show.id,
      date: show.date.toDate().toISOString().slice(0, 10),
    })
  }

  async function addTyped() {
    const query = typed.trim()
    if (!query) return
    setBusy(true)
    // Geocoded so the leg can be costed. A stop that can't be found is still
    // added — it just doesn't get a drive time, which is better than refusing
    // the stop.
    const found = await geocodeVenue(query).catch(() => null)
    setBusy(false)
    addStop({
      key: `place-${Date.now()}`,
      label: query,
      detail: found ? '' : 'Not found on the map',
      lat: found?.lat ?? null,
      lon: found?.lon ?? null,
      date: stops[stops.length - 1]?.date ?? new Date().toISOString().slice(0, 10),
    })
  }

  function setLeg(key: string, patch: Partial<LegState>) {
    setLegs((current) => ({ ...current, [key]: { ...current[key], ...patch } }))
  }

  function removeStop(key: string) {
    setStops((current) => current.filter((stop) => stop.key !== key))
  }

  /**
   * Writes the chain as itinerary items.
   *
   * A leg anchored to "arrive by" has its departure worked backwards through
   * the drive time; one anchored to "leave by" runs forwards. Either way what
   * lands on the itinerary is a real departure and a real arrival, because
   * those are the two things anybody actually reads off it.
   */
  async function save() {
    if (!uid || stops.length < 2) return
    setBusy(true)
    setError(null)
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone

    try {
      for (let index = 0; index < stops.length - 1; index++) {
        const from = stops[index]
        const to = stops[index + 1]
        const leg = legs[legKey(from, to)]
        if (!leg || !leg.time) continue

        const hours = leg.hours ?? 0
        const base = new Date(`${to.date}T${leg.time}:00`)
        if (Number.isNaN(base.getTime())) continue

        const depart =
          leg.anchor === 'arrive' ? new Date(base.getTime() - hours * 3_600_000) : base
        const arrive =
          leg.anchor === 'arrive' ? base : new Date(base.getTime() + hours * 3_600_000)

        const iso = (date: Date) => date.toISOString().slice(0, 10)
        const clock = (date: Date) =>
          `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`

        await createItineraryItem(
          {
            title: leg.mode === 'fly' ? `Fly to ${to.label}` : `Drive to ${to.label}`,
            type: leg.mode === 'fly' ? 'flight' : 'travel',
            date: iso(depart),
            time: clock(depart),
            timezone: zone,
            subtitle:
              leg.mode === 'drive' && leg.km
                ? `${Math.round(leg.km)} km · ${hours.toFixed(1)}h from ${from.label}`
                : `From ${from.label}`,
          },
          tour.id,
          uid,
        )

        await createItineraryItem(
          {
            title: `Arrive ${to.label}`,
            type: 'arrival',
            date: iso(arrive),
            time: clock(arrive),
            timezone: zone,
            subtitle: to.detail || undefined,
          },
          tour.id,
          uid,
        )
      }
      onClose()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save the route.')
    } finally {
      setBusy(false)
    }
  }

  const used = new Set(stops.map((stop) => stop.showId).filter(Boolean))
  const ready = stops.length >= 2

  return (
    <Modal
      open
      title="Build a route"
      onClose={onClose}
      width={620}
      footer={
        <div className="spread" style={{ width: '100%', alignItems: 'center' }}>
          <span className="t-caption">
            {stops.length < 2 ? 'Add at least two stops.' : `${stops.length - 1} ${stops.length === 2 ? 'leg' : 'legs'}`}
          </span>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-quiet" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" disabled={!ready || busy} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Add to itinerary'}
            </button>
          </div>
        </div>
      }
    >
      <div className="stack" style={{ gap: 14 }}>
        {stops.length === 0 && (
          <p className="t-body dim">
            Chain the stops together. Shows are one tap; anywhere else you can type. Each leg is a
            drive or a flight, and you say when you need to be there.
          </p>
        )}

        {/* ------------------------------------------------------- chain */}
        <div className="stack" style={{ gap: 0 }}>
          {stops.map((stop, index) => {
            const next = stops[index + 1]
            const leg = next ? legs[legKey(stop, next)] : null
            return (
              <div key={stop.key} className="stack" style={{ gap: 0 }}>
                <div
                  className="row panel"
                  style={{ gap: 11, padding: '11px 13px', alignItems: 'center' }}
                >
                  <Icon
                    name={stop.showId ? 'mic' : 'mapPin'}
                    size={15}
                    style={{ color: 'var(--text-tertiary)' }}
                  />
                  <span className="grow stack" style={{ gap: 1, minWidth: 0 }}>
                    <span className="truncate" style={{ fontWeight: 600 }}>
                      {stop.label}
                    </span>
                    {stop.detail && <span className="t-caption truncate">{stop.detail}</span>}
                  </span>
                  <button
                    className="btn btn-quiet"
                    aria-label={`Remove ${stop.label}`}
                    onClick={() => removeStop(stop.key)}
                  >
                    <Icon name="close" size={14} />
                  </button>
                </div>

                {/* --------------------------------------------- the leg */}
                {next && leg && (
                  <div
                    className="stack"
                    style={{
                      gap: 9,
                      margin: '0 0 0 20px',
                      padding: '11px 0 11px 16px',
                      borderLeft: '2px solid var(--hairline)',
                    }}
                  >
                    <div className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Toggle
                        value={leg.mode}
                        onChange={(mode) => setLeg(legKey(stop, next), { mode })}
                      />
                      {leg.mode === 'drive' && (
                        <span className="t-caption">
                          {leg.loading
                            ? 'Working out the drive…'
                            : leg.hours !== null
                              ? `${Math.round(leg.km ?? 0)} km · ${leg.hours.toFixed(1)}h`
                              : stop.lat === null || next.lat === null
                                ? 'No location, so no drive time'
                                : 'No road route'}
                        </span>
                      )}
                    </div>

                    <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <select
                        className="input"
                        value={leg.anchor}
                        onChange={(event) =>
                          setLeg(legKey(stop, next), { anchor: event.target.value as Anchor })
                        }
                        style={{ width: 120 }}
                      >
                        <option value="arrive">Arrive by</option>
                        <option value="leave">Leave by</option>
                      </select>
                      <input
                        className="input"
                        type="time"
                        value={leg.time}
                        onChange={(event) =>
                          setLeg(legKey(stop, next), { time: event.target.value })
                        }
                        style={{ width: 120 }}
                      />
                      <span className="t-caption">{formatDayLong(new Date(next.date))}</span>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* ------------------------------------------------------ picker */}
        {picking ? (
          <div className="panel panel-pad stack" style={{ gap: 12 }}>
            <div className="stack" style={{ gap: 6 }}>
              <span className="t-eyebrow">Somewhere else</span>
              <div className="row" style={{ gap: 8 }}>
                <input
                  className="input grow"
                  placeholder="City, airport or address"
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void addTyped()
                  }}
                />
                <button className="btn btn-secondary" disabled={busy} onClick={() => void addTyped()}>
                  Add
                </button>
              </div>
            </div>

            {sortedShows.filter((show) => !used.has(show.id)).length > 0 && (
              <div className="stack" style={{ gap: 6 }}>
                <span className="t-eyebrow">Shows on this tour</span>
                <div className="stack" style={{ gap: 5, maxHeight: 210, overflowY: 'auto' }}>
                  {sortedShows
                    .filter((show) => !used.has(show.id))
                    .map((show) => (
                      <button
                        key={show.id}
                        className="row panel"
                        style={{ padding: '9px 11px', gap: 10, cursor: 'pointer', textAlign: 'left' }}
                        onClick={() => addShow(show)}
                      >
                        <span className="t-spec-caption faint" style={{ minWidth: 86 }}>
                          {formatDayLong(show.date, show.timezone)}
                        </span>
                        <span className="grow truncate" style={{ fontWeight: 600 }}>
                          {show.city}
                        </span>
                        <span className="t-caption truncate">{show.venueName}</span>
                      </button>
                    ))}
                </div>
              </div>
            )}

            <button className="btn btn-quiet" onClick={() => setPicking(false)}>
              Done
            </button>
          </div>
        ) : (
          <button
            className="btn btn-secondary"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setPicking(true)}
          >
            <Icon name="plus" size={15} /> Add a stop
          </button>
        )}

        {error && <p className="t-caption error">{error}</p>}
      </div>
    </Modal>
  )
}

/** Drive or fly, as one control rather than two radio buttons. */
function Toggle({ value, onChange }: { value: Mode; onChange: (next: Mode) => void }) {
  return (
    <div
      className="row"
      style={{ gap: 3, padding: 3, borderRadius: 999, background: 'var(--panel-raised)' }}
    >
      {(['drive', 'fly'] as Mode[]).map((mode) => {
        const on = mode === value
        return (
          <button
            key={mode}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(mode)}
            className="chip"
            style={{
              cursor: 'pointer',
              border: 0,
              background: on ? 'var(--panel)' : 'transparent',
              color: on ? 'var(--accent-text)' : 'var(--text-secondary)',
              boxShadow: on ? 'var(--shadow-card)' : undefined,
            }}
          >
            {mode === 'drive' ? 'Drive' : 'Fly'}
          </button>
        )
      })}
    </div>
  )
}
